import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  GetWhatsAppGatewayStatus,
  GetWhatsAppActivityLogs,
  PostWhatsAppLogout,
  PostWhatsAppToggle,
} from "../Api";
import { ServiceQueryOptions } from "./query_helpers";

// ─── Query Keys ───────────────────────────────────────────────────────────────

export const whatsappQueryKeys = {
  gatewayStatus: () => ["whatsapp", "gateway-status"] as const,
  activityLogs: () => ["whatsapp", "activity-logs"] as const,
};

// ─── Query Hooks ──────────────────────────────────────────────────────────────

export const useWhatsAppGatewayStatusQuery = (options?: ServiceQueryOptions) => {
  return useQuery({
    queryKey: whatsappQueryKeys.gatewayStatus(),
    queryFn: GetWhatsAppGatewayStatus,
    refetchInterval: 3000,
    staleTime: 0,
    retry: false,
    ...options,
  });
};

export const useWhatsAppActivityLogsQuery = (options?: ServiceQueryOptions) => {
  return useQuery({
    queryKey: whatsappQueryKeys.activityLogs(),
    queryFn: GetWhatsAppActivityLogs,
    refetchInterval: 5000,
    staleTime: 0,
    retry: false,
    ...options,
  });
};

// ─── Mutation Hooks ───────────────────────────────────────────────────────────

export const useWhatsAppLogoutMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: PostWhatsAppLogout,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: whatsappQueryKeys.gatewayStatus() });
    },
  });
};

export const useWhatsAppToggleMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (enabled: boolean) => PostWhatsAppToggle(enabled),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: whatsappQueryKeys.gatewayStatus() });
    },
  });
};

// ─── Service Hook ─────────────────────────────────────────────────────────────

export const useWhatsAppService = () => {
  const gatewayStatusQuery = useWhatsAppGatewayStatusQuery();
  const activityLogsQuery = useWhatsAppActivityLogsQuery();
  const logoutMutation = useWhatsAppLogoutMutation();
  const toggleMutation = useWhatsAppToggleMutation();

  return {
    gatewayStatusQuery,
    activityLogsQuery,
    logoutMutation,
    toggleMutation,
  };
};
